import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useRenameCdnService } from '@/api/manticore/cdn-providers';
import { trackFeatureAction } from '@/lib/analytics-events';

const MAX_NAME_LEN = 255;

/**
 * Compact dialog to rename a CDN orchestrator. Only the display name is changed —
 * the provider, status and project assignment are untouched. Mirrors the
 * UmbrellaAssignProjectDialog pattern.
 */
export function CdnRenameServiceDialog({
  open,
  onOpenChange,
  serviceId,
  currentName,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  currentName: string;
}) {
  const { show } = useToast();
  const [name, setName] = useState(currentName);
  const rename = useRenameCdnService(serviceId);

  // Re-seed the field with the latest name each time the dialog opens.
  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const trimmed = name.trim();
  const invalid = trimmed.length === 0 || trimmed.length > MAX_NAME_LEN;
  const unchanged = trimmed === currentName.trim();

  const submit = () => {
    if (invalid || unchanged) return;
    rename.mutate(trimmed, {
      onSuccess: (res) => {
        trackFeatureAction('cdn', 'provider', 'edit');
        show({ tone: 'success', title: `Renamed to ${res.service_name}` });
        onOpenChange(false);
      },
      onError: () => show({ tone: 'danger', title: 'Failed to rename orchestrator' }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename orchestrator</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Choose a display name for this CDN orchestrator. This changes the
              name only — providers and delivery are unaffected.
            </p>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Orchestrator name"
              maxLength={MAX_NAME_LEN}
              autoFocus
              aria-label="Orchestrator name"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={rename.isPending || invalid || unchanged} onClick={submit}>
            {rename.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
